import { parentGradient } from "./toolGradients.js";
import { TOKENS } from "./tokens.js";

// ─── SuiteMark — the ONE reusable parent-suite mark ────────────────
//
// Spec (locked, reconciled Icon & Branding doc + Ian's 46px review):
//  - White OUTLINE letter (fill:none), stroke-width 1.6 — NEVER
//    thickened (the §4 thickening lesson: thickening strangled the
//    Ch/Ma/Ph counters and was the real cause of small-size mush).
//  - Glyph fill ratio of the tile: single-letter .64 · two-letter .54
//    (Ian's Row-B choice, applied at EVERY size, not just favicon).
//  - Two-letter marks (Ch/Ma/Ph) get +1.0 letter-spacing at all sizes
//    so counters stay open (Ian: spacing on ALL two-letter tiles, not
//    only 46px — it's how the mark is constructed, not a size patch).
//  - Solid gradient tile, rounded; gradient = suite hue → deepened
//    SAME hue (parentGradient; a parent is its own anchor, does NOT
//    resolve toward family violet).
//  - Uniform across all six suites. Used everywhere a parent mark
//    appears: launcher, suite headers, nav/back, favicon/app icon.
//
// V57.2: NerdHistory uses a custom SVG path for "H" instead of font
// rendering. DM Sans Bold "H" outline-stroked at launcher tile size
// renders as three disconnected rectangles in some browsers (notably
// Brave/Windows) because the three strokes of the H glyph render as
// independent rectangle outlines rather than one connected letter shape.
// The custom path is one continuous closed outline of the H glyph, which
// strokes as a single coherent letter shape regardless of font/browser.
//
// Letter taxonomy (intentional namespace — do NOT "fix"):
//   single letter = standalone discipline: N, T, B, G, H
//   two-letter   = the science + humanities cluster:   Ch, Ma, Ph, Mu
const LETTER = {
  stack: "N", tools: "T", bio: "B", games: "G", history: "H",
  chem: "Ch", math: "Ma", phys: "Ph", music: "Mu",
};

// V57.2 — Return the H glyph as a single-path SVG outline, sized to the
// given tile size. V57.3 tune: proportions matched to DM Sans Bold B's
// rendered bounding box so H sits at the same visual scale as other
// single letters. Cap height = size × 0.454 (was 0.64 in V57.2, which
// used the em-box height; the actual inked cap height of DM Sans Bold
// at 0.64 font-size is ~0.454 of tile). Width = size × 0.31. Bar
// thickness = 0.20 × cap height. Crossbar thickness = 0.18 × cap height.
// The path is one closed shape traced clockwise around the outside of
// the H letterform, so stroking it produces a single coherent letter.
function hPathFor(size) {
  const capHeight = size * 0.454;
  const width = size * 0.31;
  const barW = capHeight * 0.20;
  const crossH = capHeight * 0.18;
  const cx = size / 2;
  const cy = size / 2 + size * 0.02;  // match font baseline offset
  const xL = cx - width / 2;
  const xR = cx + width / 2;
  const yT = cy - capHeight / 2;
  const yB = cy + capHeight / 2;
  const xLin = xL + barW;        // right edge of left bar
  const xRin = xR - barW;        // left edge of right bar
  const yCT = cy - crossH / 2;   // top of crossbar
  const yCB = cy + crossH / 2;   // bottom of crossbar
  const f = (n) => n.toFixed(2);
  return (
    `M ${f(xL)} ${f(yT)} ` +
    `L ${f(xL)} ${f(yB)} ` +
    `L ${f(xLin)} ${f(yB)} ` +
    `L ${f(xLin)} ${f(yCB)} ` +
    `L ${f(xRin)} ${f(yCB)} ` +
    `L ${f(xRin)} ${f(yB)} ` +
    `L ${f(xR)} ${f(yB)} ` +
    `L ${f(xR)} ${f(yT)} ` +
    `L ${f(xRin)} ${f(yT)} ` +
    `L ${f(xRin)} ${f(yCT)} ` +
    `L ${f(xLin)} ${f(yCT)} ` +
    `L ${f(xLin)} ${f(yT)} ` +
    `Z`
  );
}

export default function SuiteMark({ suite, size = 64, radius }) {
  const letter = LETTER[suite] || "N";
  const two = letter.length > 1;
  const { from, to, id } = parentGradient(suite);
  const uid = `${id}-${size}`;
  const r = radius != null ? radius : size * TOKENS.suiteRadiusRatio;
  const fontSize = size * (two ? 0.54 : 0.64);
  // tracking scales with size; +1.0 at the 46px reference, proportional
  const tracking = two ? (size / 46) * 1.0 : 0;
  // stroke is FIXED 1.0 at the 46px reference and scales proportionally
  // so its visual weight is constant at every render size. (Ian-approved
  // thinner weight — the prior 1.6 + 800 was the rejected chunky version
  // that shipped by mistake; this is the original intended thinness.)
  const stroke = (size / 46) * 1.0;

  // V57.2: history H renders as a custom path, not a font glyph.
  const isHistory = suite === "history";

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ display: "block" }}>
      <defs>
        <linearGradient id={uid} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={from} />
          <stop offset="1" stopColor={to} />
        </linearGradient>
      </defs>
      <rect width={size} height={size} rx={r} fill={`url(#${uid})`} />
      {isHistory ? (
        <path
          d={hPathFor(size)}
          fill="none"
          stroke="#fff"
          strokeWidth={stroke}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      ) : (
        <text
          x={size / 2}
          y={size / 2 + size * 0.02}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={fontSize}
          fontWeight="700"
          fill="none"
          stroke="#fff"
          strokeWidth={stroke}
          strokeLinejoin="round"
          fontFamily="'DM Sans', system-ui, sans-serif"
          {...(two ? { letterSpacing: tracking } : {})}
        >
          {letter}
        </text>
      )}
    </svg>
  );
}

export { LETTER };
export { hPathFor };
